import { useCallback, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { callingService } from '../services/callingService';
import { connectionsApi } from '../services/connectionsApi';
import { getAccessToken } from '../utils/authTokens';
import type {
  Connection,
  PresenceSelfEvent,
  PresenceSnapshotEvent,
  PresenceUpdateEvent,
} from '../types/connections';

type PresenceSnapshot = {
  signalingConnected: boolean;
  reconnecting: boolean;
  selfActive: boolean;
  byUserId: Map<number, boolean>;
};

let presenceByUserId = new Map<number, boolean>();
let signalingConnected = false;
let reconnecting = false;
/** How accepted connections currently see you. */
let selfActive = true;
let boundSocket: Socket | null = null;
let lastSnapshotAt = 0;
let snapshotWatchdog: ReturnType<typeof setTimeout> | null = null;
let lifecycleBound = false;
const subscribers = new Set<() => void>();

/** If gateway snapshot does not arrive after connect, request one via socket. */
const SNAPSHOT_WATCHDOG_MS = 1500;
/** When tab becomes visible again, resync peers if snapshot is older than this. */
const VISIBILITY_RESYNC_STALE_MS = 30_000;

function emitChange() {
  subscribers.forEach((listener) => listener());
}

function clearSnapshotWatchdog() {
  if (snapshotWatchdog) {
    clearTimeout(snapshotWatchdog);
    snapshotWatchdog = null;
  }
}

function setSelfActive(active: boolean) {
  if (selfActive === active) return;
  selfActive = active;
  emitChange();
}

function setPeerActive(userId: number, active: boolean) {
  const previous = presenceByUserId.get(userId);
  if (previous === active) return;
  presenceByUserId.set(userId, active);
  emitChange();
}

function applySnapshot(event: PresenceSnapshotEvent) {
  const next = new Map(presenceByUserId);
  (event.peers ?? []).forEach((peer) => {
    next.set(peer.userId, peer.active);
  });
  // Public professionals may be present for Call/Purohit lists
  (event.publicProfessionals ?? []).forEach((peer) => {
    next.set(peer.userId, peer.active);
  });
  presenceByUserId = next;
  if (event.self && typeof event.self.active === 'boolean') {
    selfActive = event.self.active;
  }
  lastSnapshotAt = Date.now();
  clearSnapshotWatchdog();
  emitChange();
}

function getSnapshot(): PresenceSnapshot {
  return {
    signalingConnected,
    reconnecting,
    selfActive,
    byUserId: new Map(presenceByUserId),
  };
}

/**
 * Socket-only resync. Gateway already pushes presence:snapshot on connect;
 * this is a fallback / visibility resync — never hits REST.
 */
function requestSocketPresenceSnapshot() {
  if (!callingService.getSocket()?.connected) return;
  callingService.listPresence();
}

function scheduleSnapshotWatchdog() {
  clearSnapshotWatchdog();
  snapshotWatchdog = setTimeout(() => {
    snapshotWatchdog = null;
    // Gateway should have emitted presence:snapshot on connect. If not, ask once.
    if (Date.now() - lastSnapshotAt > SNAPSHOT_WATCHDOG_MS) {
      requestSocketPresenceSnapshot();
    }
  }, SNAPSHOT_WATCHDOG_MS);
}

function bindSocketHandlers() {
  const socket = callingService.getSocket();
  if (!socket || boundSocket === socket) return;
  boundSocket = socket;

  const onConnect = () => {
    signalingConnected = true;
    reconnecting = false;
    emitChange();
    // Trust gateway auto-snapshot; only request if it does not arrive.
    scheduleSnapshotWatchdog();
  };

  const onDisconnect = (reason?: string) => {
    signalingConnected = false;
    // Keep peer map + selfActive: BE has reconnect grace; do not thrash UI offline.
    reconnecting = reason !== 'io client disconnect';
    clearSnapshotWatchdog();
    emitChange();
  };

  const onReconnectAttempt = () => {
    reconnecting = true;
    emitChange();
  };

  const onSnapshot = (event: PresenceSnapshotEvent) => {
    applySnapshot(event);
  };

  const onUpdate = (event: PresenceUpdateEvent) => {
    if (event?.userId == null) return;
    setPeerActive(event.userId, Boolean(event.active));
  };

  const onSelf = (event: PresenceSelfEvent) => {
    if (typeof event?.active !== 'boolean') return;
    setSelfActive(event.active);
  };

  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  socket.io?.on?.('reconnect_attempt', onReconnectAttempt);
  socket.on('presence:snapshot', onSnapshot);
  socket.on('presence:update', onUpdate);
  socket.on('presence:self', onSelf);

  if (socket.connected) onConnect();
}

function bindLifecycleHandlers() {
  if (lifecycleBound || typeof window === 'undefined') return;
  lifecycleBound = true;

  const onOnline = () => {
    const token = getAccessToken();
    if (!token) return;
    ensurePresenceConnected(token);
  };

  const onOffline = () => {
    signalingConnected = false;
    reconnecting = true;
    emitChange();
  };

  const onVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    const token = getAccessToken();
    if (!token) return;

    const socket = callingService.getSocket();
    if (!socket?.connected) {
      ensurePresenceConnected(token);
      return;
    }

    // Light socket resync if we have been backgrounded long enough.
    if (Date.now() - lastSnapshotAt > VISIBILITY_RESYNC_STALE_MS) {
      requestSocketPresenceSnapshot();
    }
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisibility);
}

function clearPresenceState() {
  clearSnapshotWatchdog();
  presenceByUserId = new Map();
  signalingConnected = false;
  reconnecting = false;
  selfActive = true;
  boundSocket = null;
  lastSnapshotAt = 0;
  emitChange();
}

export function ensurePresenceConnected(token = getAccessToken() || localStorage.getItem('token')) {
  if (!token) return null;
  const socket = callingService.connect(token);
  bindSocketHandlers();
  bindLifecycleHandlers();
  return socket;
}

export function disconnectPresence() {
  callingService.disconnect();
  clearPresenceState();
}

export function seedPresenceFromConnections(
  accepted: Connection[],
  currentUserId: number | null,
) {
  if (currentUserId == null) return;
  let changed = false;
  accepted.forEach((connection) => {
    if (typeof connection.isActive !== 'boolean') return;
    const peerId =
      connection.initiatingUserId === currentUserId
        ? connection.acceptingUserId
        : connection.initiatingUserId;
    if (peerId === currentUserId) return;
    if (presenceByUserId.get(peerId) === connection.isActive) return;
    presenceByUserId.set(peerId, connection.isActive);
    changed = true;
  });
  if (changed) emitChange();
}

/**
 * Prefer socket snapshot. REST only when socket is down (self status only).
 */
export function refreshPresenceSocketFirst() {
  ensurePresenceConnected();
  const socket = callingService.getSocket();
  if (socket?.connected) {
    requestSocketPresenceSnapshot();
    return;
  }
  void connectionsApi
    .getPresence()
    .then((status) => setSelfActive(Boolean(status.active)))
    .catch(() => undefined);
}

export function usePresence() {
  const [snapshot, setSnapshot] = useState<PresenceSnapshot>(getSnapshot);
  const [updatingSelf, setUpdatingSelf] = useState(false);

  useEffect(() => {
    const sync = () => setSnapshot(getSnapshot());
    subscribers.add(sync);
    ensurePresenceConnected();
    sync();
    return () => {
      subscribers.delete(sync);
    };
  }, []);

  const isPeerActive = useCallback(
    (userId: number) => snapshot.byUserId.get(userId) === true,
    [snapshot.byUserId],
  );

  const refreshPresence = useCallback(() => {
    refreshPresenceSocketFirst();
  }, []);

  const setAppearActive = useCallback(async (active: boolean) => {
    setUpdatingSelf(true);
    // Optimistic UI; presence:self / REST response confirm.
    setSelfActive(active);
    try {
      ensurePresenceConnected();
      if (callingService.getSocket()?.connected) {
        callingService.setPresence(active);
      } else {
        const status = await connectionsApi.setPresence(active);
        setSelfActive(Boolean(status.active));
      }
    } catch {
      // Re-sync from server if the preference change failed.
      try {
        if (callingService.getSocket()?.connected) {
          requestSocketPresenceSnapshot();
        } else {
          const status = await connectionsApi.getPresence();
          setSelfActive(Boolean(status.active));
        }
      } catch {
        // ignore
      }
      throw new Error('Failed to update presence preference');
    } finally {
      setUpdatingSelf(false);
    }
  }, []);

  return {
    signalingConnected: snapshot.signalingConnected,
    reconnecting: snapshot.reconnecting,
    selfActive: snapshot.selfActive,
    presenceByUserId: snapshot.byUserId,
    isPeerActive,
    refreshPresence,
    setAppearActive,
    updatingSelf,
  };
}
